import React, { useEffect, useMemo, useRef, useState } from 'react'

import styles from './style.module.css'

const normalizeForSearch = (s: string): string =>
  s
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ァ-ヶ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60))

export type ComboBoxOption = {
  label: string
  value: string
}

export type ComboBoxChange = {
  label: string
  value: string | null
}

type Props = {
  options: ComboBoxOption[]
  value: string
  onChange: (next: ComboBoxChange) => void
  placeholder?: string
  disabled?: boolean
  isLocked?: boolean
  onClear?: () => void
  isLoading?: boolean
  id?: string
  ariaLabel?: string
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
  maxLength?: number
}

export const ComboBox: React.FC<Props> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  isLocked = false,
  onClear,
  isLoading = false,
  id,
  ariaLabel,
  inputMode,
  maxLength,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isComposing, setIsComposing] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useMemo(() => `${id ?? 'combobox'}-listbox`, [id])

  const filteredOptions = useMemo(() => {
    if (!value) return options
    const needle = normalizeForSearch(value)
    return options.filter((opt) => normalizeForSearch(opt.label).includes(needle))
  }, [options, value])

  // Note: ドロップダウン展開中に外側の要素がタップ/クリックされたら閉じる。
  // mousedown だとタッチ環境やネイティブ select のピッカー起動時に発火しない
  // ケースがあるため、pointerdown + touchstart の二段で確実に拾う。
  // capture フェーズで実行することで、React の合成イベントが走る前に
  // 「ラッパー内か外か」の判定を確定させる。
  // さらに、ネイティブ <select> タップ時に pointer/touch イベントが
  // 抑制されるケース・キーボード Tab・プログラム的フォーカス移動を
  // 取りこぼさないよう focusin も併用する（focus と違い bubble するので
  // document で受けられる）。
  useEffect(() => {
    if (!isOpen) return
    const handleOutside = (e: Event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handleOutside, true)
    document.addEventListener('touchstart', handleOutside, true)
    document.addEventListener('focusin', handleOutside, true)
    return () => {
      document.removeEventListener('pointerdown', handleOutside, true)
      document.removeEventListener('touchstart', handleOutside, true)
      document.removeEventListener('focusin', handleOutside, true)
    }
  }, [isOpen])

  // Note: 開閉時・候補リスト変化時にキーボード操作のアクティブ位置をリセットする。
  // - 閉じた瞬間にリセット: 閉じ直した次のフォーカスで前回の activeIndex が残り、
  //   Enter キーで意図しない候補が選ばれるのを防ぐ。
  // - 絞り込みで件数が減ったときもリセット: activeIndex が範囲外を指して
  //   aria-activedescendant が存在しない option ID を参照するのを防ぐ。
  useEffect(() => {
    setActiveIndex(-1)
  }, [isOpen, filteredOptions.length])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ label: e.target.value, value: null })
    if (!isOpen) setIsOpen(true)
  }

  const handleSelect = (opt: ComboBoxOption) => {
    onChange({ label: opt.label, value: opt.value })
    setIsOpen(false)
    inputRef.current?.blur()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isComposing) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!isOpen) setIsOpen(true)
      setActiveIndex((idx) => Math.min(idx + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((idx) => Math.max(idx - 1, 0))
    } else if (e.key === 'Enter') {
      if (!isOpen) return
      e.preventDefault()
      const target =
        activeIndex >= 0 && activeIndex < filteredOptions.length
          ? filteredOptions[activeIndex]
          : filteredOptions[0]
      if (target) handleSelect(target)
      else setIsOpen(false)
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className={`${styles.input} ${isLocked ? styles.locked : ''}`}
        value={value}
        onChange={handleInputChange}
        onFocus={() => !isLocked && !disabled && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={isLocked}
        inputMode={inputMode}
        maxLength={maxLength}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        autoComplete="off"
      />
      {isLocked && onClear ? (
        <button
          type="button"
          className={styles.clearButton}
          onClick={onClear}
          aria-label="クリア"
          disabled={disabled}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      ) : (
        <svg
          className={styles.searchIcon}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l4.25 4.25a1 1 0 0 0 1.41-1.41L15.5 14zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
        </svg>
      )}

      {isOpen && !isLocked && !disabled && (
        <ul id={listboxId} role="listbox" className={styles.dropdown}>
          {isLoading ? (
            <li className={styles.loading}>読み込み中…</li>
          ) : filteredOptions.length === 0 ? (
            <li className={styles.empty}>候補なし</li>
          ) : (
            filteredOptions.map((opt, idx) => (
              <li
                key={opt.value}
                id={`${listboxId}-option-${idx}`}
                role="option"
                aria-selected={idx === activeIndex}
                className={`${styles.option} ${idx === activeIndex ? styles.optionActive : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(opt)
                }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
